import { Int64 } from "../types/int64";

function _ppBuffer(buf: any, buflen: any) {
    let out = "";
    for (let i = 0; i < buflen; ++i) {
        if (i > 0) out += " ";
        let byte = buf[i].toString(16);
        if (byte.length < 2) {
            byte = "0" + byte;
        }
        out += byte;
    }
    return out;
}

export class RosePacket {
    cmd: any;
    dataLength: number;
    data: Uint8Array;
    readPos: number;
    view: DataView;

    constructor(cmd: any, capacity: number = 4096) {
        this.cmd = cmd;
        this.dataLength = 0;
        this.data = new Uint8Array(capacity);
        this.readPos = 0;
        this.view = new DataView(this.data.buffer);
    }

    setInt8(pos: any, val: any) {
        this.view.setInt8(pos, val);
    }

    setInt16(pos: any, val: any) {
        this.view.setInt16(pos, val, true);
    }

    setInt32(pos: any, val: any) {
        this.view.setInt32(pos, val, true);
    }

    setUint8(pos: any, val: any) {
        this.view.setUint8(pos, val);
    }

    setUint16(pos: any, val: any) {
        this.view.setUint16(pos, val, true);
    }

    setUint32(pos: any, val: any) {
        this.view.setUint32(pos, val, true);
    }

    setUint64(pos: any, val: any) {
        this.setUint32(pos, val.lo);
        this.setUint32(pos + 4, val.hi);
    }

    setFloat(pos: any, val: any) {
        this.view.setFloat32(pos, val, true);
    }

    addInt8(val: any) {
        this.view.setInt8(this.dataLength, val);
        this.dataLength += 1;
    }

    addInt16(val: any) {
        this.view.setInt16(this.dataLength, val, true);
        this.dataLength += 2;
    }

    addInt32(val: any) {
        this.view.setInt32(this.dataLength, val, true);
        this.dataLength += 4;
    }

    addUint8(val: any) {
        this.view.setUint8(this.dataLength, val);
        this.dataLength += 1;
    }

    addUint16(val: any) {
        this.view.setUint16(this.dataLength, val, true);
        this.dataLength += 2;
    }

    addUint32(val: any) {
        this.view.setUint32(this.dataLength, val, true);
        this.dataLength += 4;
    }

    addUint64(val: any) {
        this.addUint32(val.lo);
        this.addUint32(val.hi);
    }

    addFloat(val: any) {
        this.view.setFloat32(this.dataLength, val, true);
        this.dataLength += 4;
    }

    addString(val: any, noNullTerm?: any) {
        for (let i = 0; i < val.length; i++) {
            this.addUint8(val.charCodeAt(i) & 0xff);
        }
        if (!noNullTerm) {
            this.addUint8(0);
        }
    }

    getInt8(pos: any) {
        return this.view.getInt8(pos);
    }

    getInt16(pos: any) {
        return this.view.getInt16(pos, true);
    }

    getInt32(pos: any) {
        return this.view.getInt32(pos, true);
    }

    getUint8(pos: any) {
        return this.view.getUint8(pos);
    }

    getUint16(pos: any) {
        return this.view.getUint16(pos, true);
    }

    getUint32(pos: any) {
        return this.view.getUint32(pos, true);
    }

    skip(num: any) {
        this.readPos += num;
    }

    readInt8() {
        const val = this.view.getInt8(this.readPos);
        this.readPos += 1;
        return val;
    }

    readInt16() {
        const val = this.view.getInt16(this.readPos, true);
        this.readPos += 2;
        return val;
    }

    readInt32() {
        const val = this.view.getInt32(this.readPos, true);
        this.readPos += 4;
        return val;
    }

    readUint8() {
        const val = this.view.getUint8(this.readPos);
        this.readPos += 1;
        return val;
    }

    readUint16() {
        const val = this.view.getUint16(this.readPos, true);
        this.readPos += 2;
        return val;
    }

    readUint32() {
        const val = this.view.getUint32(this.readPos, true);
        this.readPos += 4;
        return val;
    }

    readUint64() {
        const lo = this.readUint32();
        const hi = this.readUint32();
        return new Int64(lo, hi);
    }

    readFloat() {
        const val = this.view.getFloat32(this.readPos, true);
        this.readPos += 4;
        return val;
    }

    readVector2() {
        const x = this.readFloat();
        const y = this.readFloat();
        return new THREE.Vector2(x, y);
    }

    readPartItem() {
        const item: any = {};
        item.itemNo = this.readUint32();
        item.gemOption1 = this.readUint16();
        item.gemOption2 = this.readUint16();
        item.gemOption3 = this.readUint16();
        item.socketCount = this.readUint8();
        item.refineGrade = this.readUint16();
        item.color = this.readUint32();
        return item;
    }

    readVisItem() {
        const item: any = {};
        item.itemType = this.readUint16();
        item.itemNo = this.readUint32();
        item.charDbId = this.readUint32();
        /* reserved */ this.readUint32();
        item.color = this.readUint32();
        return item;
    }

    readItem() {
        const item = this.readVisItem();
        item.itemKey = this.readUint64();
        item.isCrafted = this.readUint8();
        item.gemOption1 = this.readUint16();
        item.gemOption2 = this.readUint16();
        item.gemOption3 = this.readUint16();
        item.durability = this.readUint16();
        item.itemLife = this.readUint16();
        item.socketCount = this.readUint8();
        item.isAppraised = this.readUint8();
        item.refineGrade = this.readUint16();
        item.quantity = this.readUint16();
        item.location = this.readUint8();
        item.slotNo = this.readUint32();
        /*pickupTime*/ this.skip(14);
        item.timeRemaining = this.readUint32();
        item.moveLimits = this.readUint16();
        item.bindOnAcquire = this.readUint8();
        item.bindOnEquipUse = this.readUint8();
        item.money = this.readUint32();
        return item;
    }

    readDropItem() {
        const item: any = {};
        item.position = this.readVector2().divideScalar(100);
        item.item = this.readItem();
        item.objectIdx = this.readUint16();
        item.ownerObjectIdx = this.readUint16();
        return item;
    }

    readString() {
        const startPos = this.readPos;
        while (this.readUint8());
        const strArray = this.data.subarray(startPos, this.readPos - 1);
        return String.fromCharCode.apply(null, Array.from(strArray));
    }

    isReadEof() {
        return this.readPos >= this.dataLength;
    }

    toBuffer() {
        const outBuf = new Uint8Array(6 + this.dataLength);
        const outView = new DataView(outBuf.buffer);
        outView.setUint16(0, outBuf.length, true);
        outView.setUint16(2, this.cmd, true);
        outView.setUint16(4, 0x58d1, true);
        for (let i = 0; i < this.dataLength; ++i) {
            outBuf[6 + i] = this.data[i];
        }
        return outBuf;
    }

    toString(maxLen: any) {
        return _ppBuffer(this.data, maxLen);
    }
}

